import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { clamp, drawPanel, drawScanlines, roundedRectPath, setText } from "./drawing";

const ROUND_SECONDS = 60;

type CheckpointKind = "healthy" | "failing";

interface Checkpoint {
  lane: number;
  depth: number;
  kind: CheckpointKind;
}

export class OneClientRiderGame implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0x0c1d);
  private readonly checkpoints: Checkpoint[] = [];
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private distance = 0;
  private playerPosition = 0;
  private playerVelocity = 0;
  private spawnTimer = 0.5;
  private posture = 1;
  private stabilizeEnergy = 1;
  private stabilizing = false;
  private shake = 0;
  private message = "Hit healthy checkpoints and keep device posture up";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {}

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    this.shake = Math.max(0, this.shake - deltaSeconds);
    if (this.messageTimer === 0) this.message = "Hit healthy checkpoints and keep device posture up";

    if (this.time === 0) {
      this.finish("Posture held to the finish");
      return;
    }

    this.updateSteering(deltaSeconds);
    this.updateRoute(deltaSeconds);
    if (this.ended) return;
    this.decayPosture(deltaSeconds);
  }

  render(context: CanvasRenderingContext2D): void {
    context.save();
    if (this.shake > 0 && !this.services.reducedMotion) {
      context.translate(Math.sin(this.elapsed * 90) * 6, Math.cos(this.elapsed * 70) * 4);
    }
    this.drawWorld(context);
    const ordered = [...this.checkpoints].sort((left, right) => right.depth - left.depth);
    for (const checkpoint of ordered) this.drawCheckpoint(context, checkpoint);
    this.drawVehicle(context);
    context.restore();
    this.drawTelemetry(context);
    this.drawFooter(context);
    drawScanlines(context, "rgba(255, 255, 255, 0.018)");
  }

  getHud(): GameHud {
    return { score: Math.floor(this.score), time: this.time, message: this.message, ended: this.ended };
  }

  destroy(): void {}

  private updateSteering(deltaSeconds: number): void {
    const input = this.services.input;
    const direction = Number(input.isDown("right")) - Number(input.isDown("left"));
    const targetVelocity = direction * 1.6;
    this.playerVelocity += (targetVelocity - this.playerVelocity) * Math.min(1, deltaSeconds * 9);
    if (direction === 0) this.playerVelocity *= Math.max(0, 1 - deltaSeconds * 5);
    this.playerPosition = clamp(this.playerPosition + this.playerVelocity * deltaSeconds, -1, 1);

    this.stabilizing = input.isDown("primary") && this.stabilizeEnergy > 0.02;
    if (this.stabilizing) {
      this.stabilizeEnergy = Math.max(0, this.stabilizeEnergy - deltaSeconds * 0.4);
    } else {
      this.stabilizeEnergy = Math.min(1, this.stabilizeEnergy + deltaSeconds * 0.14);
    }
  }

  private decayPosture(deltaSeconds: number): void {
    const drain = this.stabilizing ? -0.05 : 0.03;
    this.posture = clamp(this.posture - drain * deltaSeconds, 0, 1);
    this.score += deltaSeconds * 60;
    if (this.posture <= 0) this.finish("Device posture failed - access revoked");
  }

  private updateRoute(deltaSeconds: number): void {
    const speed = 0.4;
    this.distance += speed * deltaSeconds;
    this.spawnTimer -= deltaSeconds;

    if (this.spawnTimer <= 0 && this.checkpoints.length < 10) {
      this.spawnCheckpoint();
      this.spawnTimer = this.random.between(0.55, 0.85);
    }

    for (let index = this.checkpoints.length - 1; index >= 0; index -= 1) {
      const checkpoint = this.checkpoints[index];
      if (!checkpoint) continue;
      checkpoint.depth -= speed * deltaSeconds;
      if (checkpoint.depth > 0.05) continue;
      this.resolveCheckpoint(checkpoint);
      this.checkpoints.splice(index, 1);
      if (this.ended) return;
    }
  }

  private spawnCheckpoint(): void {
    const lanes = [-0.78, -0.26, 0.26, 0.78] as const;
    const lane = lanes[this.random.integer(lanes.length)] ?? 0;
    this.checkpoints.push({
      lane,
      depth: 1.05,
      kind: this.random.next() < 0.58 ? "healthy" : "failing",
    });
  }

  private resolveCheckpoint(checkpoint: Checkpoint): void {
    const collision = Math.abs(checkpoint.lane - this.playerPosition) < 0.23;
    if (checkpoint.kind === "healthy" && collision) {
      this.posture = clamp(this.posture + 0.16, 0, 1);
      this.score += 130;
      this.services.audio.play("hit");
      this.setMessage("Posture check passed", 1.4);
      return;
    }
    if (checkpoint.kind === "failing" && collision) {
      this.posture = clamp(this.posture - (this.stabilizing ? 0.08 : 0.22), 0, 1);
      this.score = Math.max(0, this.score - 80);
      this.shake = 0.36;
      this.services.audio.play("miss");
      this.setMessage(this.stabilizing ? "Failing check absorbed" : "Failing device check hit", 1.7);
      if (this.posture <= 0) this.finish("Device posture failed - access revoked");
      return;
    }
    if (checkpoint.kind === "healthy") this.score += 15;
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${Math.floor(this.score)}.`);
  }

  private roadPoint(lane: number, depth: number): { x: number; y: number; scale: number } {
    const progress = 1 - clamp(depth, 0, 1);
    const curvedProgress = progress * progress;
    const halfWidth = 54 + curvedProgress * 465;
    return {
      x: 640 + lane * halfWidth,
      y: 250 + curvedProgress * 470,
      scale: 0.2 + curvedProgress * 0.95,
    };
  }

  private drawWorld(context: CanvasRenderingContext2D): void {
    const sky = context.createLinearGradient(0, 0, 0, 720);
    sky.addColorStop(0, "#0a1c14");
    sky.addColorStop(0.55, "#0f3327");
    sky.addColorStop(1, "#07120d");
    context.fillStyle = sky;
    context.fillRect(-20, -20, 1320, 760);

    const road = context.createLinearGradient(0, 260, 0, 720);
    road.addColorStop(0, "#111e18");
    road.addColorStop(1, "#050f0a");
    context.fillStyle = road;
    context.beginPath();
    context.moveTo(565, 250);
    context.lineTo(715, 250);
    context.lineTo(1160, 720);
    context.lineTo(120, 720);
    context.closePath();
    context.fill();

    context.strokeStyle = "#42e2c3";
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(565, 250);
    context.lineTo(120, 720);
    context.moveTo(715, 250);
    context.lineTo(1160, 720);
    context.stroke();

    for (const lane of [-0.5, 0, 0.5]) {
      context.strokeStyle = "rgba(157, 237, 221, 0.34)";
      context.lineWidth = 2;
      for (let segment = 0; segment < 8; segment += 1) {
        const startProgress = ((segment / 8 + this.distance * 1.8) % 1) * 0.94;
        const endProgress = Math.min(1, startProgress + 0.055 + startProgress * 0.045);
        const start = this.roadPoint(lane, 1 - startProgress);
        const end = this.roadPoint(lane, 1 - endProgress);
        context.beginPath();
        context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
        context.stroke();
      }
    }
  }

  private drawCheckpoint(context: CanvasRenderingContext2D, checkpoint: Checkpoint): void {
    const point = this.roadPoint(checkpoint.lane, checkpoint.depth);
    const size = 62 * point.scale;
    const healthy = checkpoint.kind === "healthy";
    const accent = healthy ? "#42e2c3" : "#ff5d5d";
    context.save();
    context.translate(point.x, point.y);
    context.shadowColor = accent;
    context.shadowBlur = 18 * point.scale;
    context.fillStyle = "rgba(5, 11, 20, 0.9)";
    context.strokeStyle = accent;
    context.lineWidth = Math.max(2, 4 * point.scale);
    context.beginPath();
    context.arc(0, 0, size * 0.6, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    context.strokeStyle = "#f7fbff";
    context.lineWidth = Math.max(2, 3 * point.scale);
    if (healthy) {
      context.beginPath();
      context.moveTo(-size * 0.24, 0);
      context.lineTo(-size * 0.05, size * 0.2);
      context.lineTo(size * 0.28, -size * 0.22);
      context.stroke();
    } else {
      context.beginPath();
      context.moveTo(-size * 0.2, -size * 0.2);
      context.lineTo(size * 0.2, size * 0.2);
      context.moveTo(size * 0.2, -size * 0.2);
      context.lineTo(-size * 0.2, size * 0.2);
      context.stroke();
    }
    if (point.scale > 0.45) {
      setText(context, Math.max(9, 11 * point.scale), 900, accent, "center");
      context.fillText(healthy ? "COMPLIANT" : "AT RISK", 0, size * 0.82);
    }
    context.restore();
  }

  private drawVehicle(context: CanvasRenderingContext2D): void {
    const x = 640 + this.playerPosition * 430;
    const tilt = this.playerVelocity * 0.12;
    context.save();
    context.translate(x, 625);
    context.rotate(tilt);
    context.shadowColor = this.stabilizing ? "#ff7a1a" : "#42e2c3";
    context.shadowBlur = this.stabilizing ? 32 : 17;
    context.fillStyle = "#0b2238";
    context.strokeStyle = this.stabilizing ? "#ff7a1a" : "#42e2c3";
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(0, -44);
    context.lineTo(40, 20);
    context.lineTo(40, 40);
    context.lineTo(-40, 40);
    context.lineTo(-40, 20);
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    context.fillStyle = "#f7fbff";
    context.beginPath();
    context.arc(0, -2, 11, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }

  private drawTelemetry(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 42, 330, 128, "#ff7a1a");
    setText(context, 12, 850, "#9feddd");
    context.fillText("DEVICE POSTURE", 72, 72);
    setText(context, 25, 900, "#f7fbff");
    context.fillText(this.stabilizing ? "STABILIZING" : "ON POLICY", 72, 106);
    setText(context, 11, 850, "#839aa5");
    context.fillText("STABILIZE", 72, 143);
    context.fillStyle = "rgba(255, 255, 255, 0.12)";
    roundedRectPath(context, 150, 137, 180, 12, 6);
    context.fill();
    context.fillStyle = this.stabilizing ? "#ff7a1a" : "#42e2c3";
    roundedRectPath(context, 150, 137, Math.max(3, 180 * this.stabilizeEnergy), 12, 6);
    context.fill();

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("POSTURE HEALTH", 1232, 60);
    context.fillStyle = "rgba(255, 255, 255, 0.12)";
    roundedRectPath(context, 1012, 78, 220, 16, 8);
    context.fill();
    context.fillStyle = this.posture > 0.35 ? "#42e2c3" : "#ff5d5d";
    roundedRectPath(context, 1012, 78, Math.max(3, 220 * this.posture), 16, 8);
    context.fill();
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 650, 1196, 54, "#ff7a1a");
    setText(context, 20, 850, "#f7fbff");
    context.fillText(this.message, 72, 678);
    setText(context, 12, 750, "#9cb2bd", "right");
    context.fillText("STEER  \u2190 \u2192     STABILIZE  SPACE", 1204, 678);
  }
}
